import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  getUserOptimizationData,
  completeOptimization,
  updateOptimizationProgress,
  VIP_CONFIG 
} from '../services/optimizationService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import TaskSubmissionPopup from './TaskSubmissionPopup';
import CustomerServicePopup from './CustomerServicePopup';
// Auto knife system removed - using manual knife system instead
import { getUserById } from '../firebase/helpers';

function Optimization({ onBack, vipLevel = 'VIP1', userProfile, onNavigateToDeposit }) {
  const { user } = useAuth();
  const [optimizationData, setOptimizationData] = useState(null);
  const [currentTask, setCurrentTask] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showOptimizeDone, setShowOptimizeDone] = useState(false);
  const [productCards, setProductCards] = useState([]);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [popupMessage, setPopupMessage] = useState(null);
  const [showTaskPopup, setShowTaskPopup] = useState(false);
  const [showCustomerServicePopup, setShowCustomerServicePopup] = useState(false);
  const [showComboOrderMessage, setShowComboOrderMessage] = useState(false);
  const [showPreviousRatingMessage, setShowPreviousRatingMessage] = useState(false);
  const [nextOptimization, setNextOptimization] = useState(null);
  const [hasPendingTask, setHasPendingTask] = useState(false);
  const [taskProgress, setTaskProgress] = useState(null);
  const [activeTriggers, setActiveTriggers] = useState([]);
  const [hasKnifeApplied, setHasKnifeApplied] = useState(false);
  const [knifeRemovedPendingSubmit, setKnifeRemovedPendingSubmit] = useState(false);

  const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
  const totalTasks = config.tasks;
  const maxOptimizations = config.optimizations;
  
  // Check if user has sufficient balance to start tasks (minimum $60)
  const MIN_BALANCE_FOR_OPTIMIZATION = 60;
  const walletBalance = userProfile?.balances?.wallet || 0;
  const hasSufficientBalance = walletBalance >= MIN_BALANCE_FOR_OPTIMIZATION;

  // All available images from optimization-images folder
  const allImages = [
    '/optimization-images/1st.jpg', '/optimization-images/2nd.jpg', '/optimization-images/3rd.jpg',
    '/optimization-images/4th.jpg', '/optimization-images/5th.jpg', '/optimization-images/6th.jpg',
    '/optimization-images/7th.jpg', '/optimization-images/8th.jpg', '/optimization-images/9th.jpg',
    '/optimization-images/10th.jpg', '/optimization-images/11th.jpg', '/optimization-images/12th.jpg',
    '/optimization-images/13th.jpg', '/optimization-images/14th.jpg', '/optimization-images/15th.jpg',
    '/optimization-images/16th.jpg', '/optimization-images/17th.jpg', '/optimization-images/18th.jpg',
    '/optimization-images/19th.jpg', '/optimization-images/20th.jpg', '/optimization-images/21th.jpg',
    '/optimization-images/22th.jpg', '/optimization-images/23th.jpg', '/optimization-images/24th.jpg',
    '/optimization-images/25th.jpg', '/optimization-images/26th.jpg', '/optimization-images/27th.jpg',
    '/optimization-images/28th.jpg', '/optimization-images/29th.jpg', '/optimization-images/30th.jpg',
    '/optimization-images/31th.jpg', '/optimization-images/32th.jpg', '/optimization-images/33th.avif',
    '/optimization-images/34th.avif', '/optimization-images/35th.avif', '/optimization-images/36th.avif',
    '/optimization-images/37th.avif', '/optimization-images/38th.avif', '/optimization-images/39th.avif',
    '/optimization-images/40th.avif', '/optimization-images/41th.avif', '/optimization-images/42th.avif',
    '/optimization-images/43th.avif', '/optimization-images/44th.avif', '/optimization-images/45th.avif',
    '/optimization-images/46th.avif', '/optimization-images/47th.avif', '/optimization-images/48th.avif',
    '/optimization-images/49th.avif', '/optimization-images/50th.avif', '/optimization-images/51th.avif',
    '/optimization-images/52th.avif', '/optimization-images/53th.avif', '/optimization-images/54th.avif',
    '/optimization-images/55th.avif', '/optimization-images/56th.avif', '/optimization-images/57th.avif',
    '/optimization-images/58th.avif', '/optimization-images/59th.avif', '/optimization-images/60th.avif',
    '/optimization-images/61th.avif', '/optimization-images/62th.avif', '/optimization-images/63th.avif',
    '/optimization-images/64th.avif', '/optimization-images/65th.avif', '/optimization-images/66th.avif',
    '/optimization-images/67th.avif', '/optimization-images/68th.avif', '/optimization-images/69th.avif',
    '/optimization-images/70th.avif',
  ];

  // Function to get random images
  const getRandomImages = () => {
    const shuffled = [...allImages].sort(() => Math.random() - 0.5);
    return shuffled;
  };

  // Function to update all 8 product images (excluding the center Start Button)
  const updateAllProductImages = () => {
    const newImages = getRandomImages();
    setProductCards(prevCards => {
      return prevCards.map(card => {
        // Keep the Start Button unchanged
        if (card.isStartButton) {
          return card;
        }
        // Update all other product images with new random images
        const cardIndex = prevCards.findIndex(c => c.id === card.id);
        // Map card positions to image array indices (excluding position 4 which is Start Button)
        // Positions: 0,1,2,3,4(Start),5,6,7,8
        // Image indices: 0,1,2,3,4,5,6,7
        let imageIndex = cardIndex;
        if (cardIndex > 4) {
          imageIndex = cardIndex - 1; // Adjust for Start Button at position 4
        }
        return {
          ...card,
          image: newImages[imageIndex] || allImages[imageIndex] || allImages[0]
        };
      });
    });
  };

  // Initialize product cards on component mount
  useEffect(() => {
    const initialImages = getRandomImages();
    setProductCards([
      { id: 1, name: 'Product 1', image: initialImages[0] || allImages[0] },
      { id: 2, name: 'Product 2', image: initialImages[1] || allImages[1] },
      { id: 3, name: 'Product 3', image: initialImages[2] || allImages[2] },
      { id: 4, name: 'Product 4', image: initialImages[3] || allImages[3] },
      { id: 5, name: 'Start Button', isStartButton: true },
      { id: 6, name: 'Product 6', image: initialImages[4] || allImages[4] },
      { id: 7, name: 'Product 7', image: initialImages[5] || allImages[5] },
      { id: 8, name: 'Product 8', image: initialImages[6] || allImages[6] },
      { id: 9, name: 'Product 9', image: initialImages[7] || allImages[7] },
    ]);
  }, []);

  // Reset popup states when component mounts
  useEffect(() => {
    // Reset popup-related states to ensure clean state on mount
    setShowTaskPopup(false);
    setHasPendingTask(false);
    setShowPreviousRatingMessage(false);
  }, []);


  // Real-time listener for user profile to detect knife application/removal
  useEffect(() => {
    if (!user?.uid) return;
    
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = { id: docSnapshot.id, ...docSnapshot.data() };
        const hasNegativeBalance = userData.hasNegativeBalance === true;
        const negativeBalance = userData.balances?.negative || 0;
        const knifeStatus = hasNegativeBalance || (negativeBalance < 0);
        const pendingSubmit = userData.knifeRemovedPendingSubmit || false;
        
        console.log('📊 Real-time update detected:', {
          hasNegativeBalance,
          negativeBalance,
          knifeStatus,
          pendingSubmit,
          wallet: userData.balances?.wallet,
          total: userData.balances?.total
        });
        
        // Always update state to ensure UI reflects current status
        setHasKnifeApplied(knifeStatus);
        setKnifeRemovedPendingSubmit(pendingSubmit);
        
        // If knife is applied, redirect to deposit page immediately
        if (knifeStatus && onNavigateToDeposit) {
          console.log('🔪 Knife detected - redirecting to deposit page immediately');
          onNavigateToDeposit();
        }
        // If knife is removed but pending submit, redirect to deposit page
        else if (!knifeStatus && pendingSubmit && onNavigateToDeposit) {
          console.log('🔓 Knife removed - pending submit, redirecting to deposit page');
          onNavigateToDeposit();
        }
        // If knife is removed and submitted, log for debugging
        else if (!knifeStatus && !pendingSubmit && hasKnifeApplied) {
          console.log('🔓 Knife removed and submitted - button unlocked, user can start tasks');
        }
      }
    }, (error) => {
      console.error('Error in user profile listener:', error);
    });
    
    return () => unsubscribe();
  }, [user?.uid, onNavigateToDeposit, hasKnifeApplied]);

  // Also check initial userProfile prop
  useEffect(() => {
    if (userProfile) {
      const knifeStatus = userProfile.hasNegativeBalance || (userProfile.balances?.negative && userProfile.balances.negative < 0);
      setHasKnifeApplied(knifeStatus);
      
      if (knifeStatus && onNavigateToDeposit) {
        console.log('🔪 Initial knife check - redirecting to deposit page immediately');
        onNavigateToDeposit();
      }
    }
  }, [userProfile?.hasNegativeBalance, userProfile?.balances?.negative, onNavigateToDeposit]);

  // Check for daily reset on mount
  useEffect(() => {
    const checkDailyReset = async () => {
      if (!user?.uid) return;
      
      try {
        const { checkAndResetUserProgress } = await import('../services/dailyResetService');
        await checkAndResetUserProgress(user.uid);
      } catch (error) {
        console.error('Error checking daily reset:', error);
      }
    };
    
    checkDailyReset();
  }, [user?.uid]);

  // Load optimization data on mount
  useEffect(() => {
    const loadOptimizationData = async () => {
      if (!user?.uid) return;
      
      try {
        setLoading(true);
        const data = await getUserOptimizationData(user.uid, vipLevel);
        setOptimizationData(data);
        setCurrentTask(data.currentTask || 0);
        setIsLocked(data.optimizations[data.currentOptimization]?.locked || false);
      } catch (error) {
        console.error('Error loading optimization data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOptimizationData();

    // Set up real-time listener for status updates
    if (user?.uid) {
      const statusRef = doc(db, 'optimizationStatus', user.uid);
      const unsubscribe = onSnapshot(statusRef, async (docSnapshot) => {
        if (docSnapshot.exists()) {
          try {
            const data = await getUserOptimizationData(user.uid, vipLevel);
            setOptimizationData(data);
            // Only update currentTask if it's different to avoid unnecessary re-renders
            if (data.currentTask !== undefined && data.currentTask !== currentTask) {
              setCurrentTask(data.currentTask);
            }
            setIsLocked(data.optimizations[data.currentOptimization]?.locked || false);
          } catch (error) {
            console.error('Error in real-time listener:', error);
          }
        }
      }, (error) => {
        console.error('Error in real-time listener:', error);
      });

      return () => unsubscribe();
    }
  }, [user?.uid, vipLevel]);

  // Handle task completion
  useEffect(() => {
    if (currentTask >= totalTasks && !isComplete && !popupDismissed && user?.uid && optimizationData) {
      handleOptimizationCompletion();
    }
  }, [currentTask, totalTasks, isComplete, popupDismissed, user?.uid, optimizationData]);

  const handleOptimizationCompletion = async () => {
    try {
      setIsComplete(true);
      setPopupDismissed(false);
      
      // Complete the optimization in database
      const result = await completeOptimization(
        user.uid, 
        optimizationData.currentOptimization, 
        vipLevel
      );
      
      if (result.success) {
        // Reload optimization data
        const updatedData = await getUserOptimizationData(user.uid, vipLevel);
        setOptimizationData(updatedData);
        setCurrentTask(0);
        
        // Show customer service popup if needs unlock
        if (result.needsUnlock && result.nextOptimization) {
          setNextOptimization(result.nextOptimization);
          setShowCustomerServicePopup(true);
          setPopupMessage('needsUnlock');
        } else {
          setPopupMessage('dailyLimit');
        }
      }
    } catch (error) {
      console.error('Error completing optimization:', error);
    }
  };

  const handleTaskComplete = async (taskResult) => {
    // Calculate new task number
    const newTask = (optimizationData?.currentTask || currentTask) + 1;
    
    
    // Update progress in database first
    try {
      const result = await updateOptimizationProgress(
        user.uid,
        vipLevel,
        optimizationData.currentOptimization,
        newTask,
        (optimizationData.tasksCompleted || 0) + 1
      );
      
      if (result.success) {
        // Update local state immediately for UI responsiveness
        setCurrentTask(newTask);
        
        // Update optimization data state
        setOptimizationData(prev => ({
          ...prev,
          currentTask: newTask,
          tasksCompleted: (prev?.tasksCompleted || 0) + 1,
          optimizations: {
            ...prev?.optimizations,
            [optimizationData.currentOptimization]: {
              ...prev?.optimizations?.[optimizationData.currentOptimization],
              progress: `${newTask}/${totalTasks}`
            }
          }
        }));
        
        // Change all 8 product images on every task submission
        updateAllProductImages();
        
        // Reload from database after a short delay to ensure sync
        setTimeout(async () => {
          try {
            const updatedData = await getUserOptimizationData(user.uid, vipLevel);
            setOptimizationData(updatedData);
            // Sync currentTask from database (real-time listener will also handle this)
            if (updatedData.currentTask !== undefined) {
              setCurrentTask(updatedData.currentTask);
            }
          } catch (error) {
            console.error('Error reloading optimization data:', error);
          }
        }, 300);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      // Don't update state if database update failed
    }
    
    // Show success message
    setShowOptimizeDone(true);
    setTimeout(() => {
      setShowOptimizeDone(false);
    }, 1500);
  };

  useEffect(() => {
    if (currentTask > 0 && currentTask <= totalTasks) {
      setShowOptimizeDone(true);
      const timer = setTimeout(() => {
        setShowOptimizeDone(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentTask, totalTasks]);

  const handleStart = () => {
    if (!optimizationData) return;
    
    // Check if user has sufficient balance
    if (!hasSufficientBalance) {
      alert(`Insufficient balance. You need at least $${MIN_BALANCE_FOR_OPTIMIZATION} USDT to start tasks. Your current balance is $${walletBalance.toFixed(2)} USDT.`);
      return;
    }
    
    // Check if knife is applied - if so, redirect to deposit page
    if (hasKnifeApplied) {
      if (onNavigateToDeposit) {
        console.log('🔪 Knife applied - redirecting to deposit page');
        onNavigateToDeposit();
      }
      return;
    }
    
    // Check if knife was removed but user needs to submit
    if (knifeRemovedPendingSubmit) {
      if (onNavigateToDeposit) {
        console.log('🔓 Knife removed - pending submit, redirecting to deposit page');
        onNavigateToDeposit();
      }
      return;
    }
    
    const currentOpt = optimizationData.optimizations[optimizationData.currentOptimization];
    const isCurrentOptLocked = currentOpt?.locked || false;
    
    if (currentTask < totalTasks && !isCurrentOptLocked && !isLocked && user?.uid && !hasKnifeApplied && !knifeRemovedPendingSubmit && hasSufficientBalance) {
      // Reset previous rating message when starting new task
      setShowPreviousRatingMessage(false);
      
      // Check if there's a pending task - show "previous rating" message instead of popup
      // But only if popup is actually open (not just closed due to knife redirect)
      if (hasPendingTask && showTaskPopup) {
        setShowPreviousRatingMessage(true);
        return;
      }
      
      // No pending task or popup was closed, open the popup and set pending task
      setShowTaskPopup(true);
      setHasPendingTask(true);
    }
  };

  const handleReset = async () => {
    if (!isLocked && user?.uid && optimizationData) {
      setCurrentTask(0);
      setIsComplete(false);
      setPopupDismissed(false);
      setPopupMessage(null);
      
      // Reset progress in database
      try {
        await updateOptimizationProgress(
          user.uid,
          vipLevel,
          optimizationData.currentOptimization,
          0,
          0
        );
        
        // Reload optimization data
        const updatedData = await getUserOptimizationData(user.uid, vipLevel);
        setOptimizationData(updatedData);
      } catch (error) {
        console.error('Error resetting progress:', error);
      }
      
      // Reset images to new random set
      const newImages = getRandomImages();
      setProductCards([
        { id: 1, name: 'Product 1', image: newImages[0] || allImages[0] },
        { id: 2, name: 'Product 2', image: newImages[1] || allImages[1] },
        { id: 3, name: 'Product 3', image: newImages[2] || allImages[2] },
        { id: 4, name: 'Product 4', image: newImages[3] || allImages[3] },
        { id: 5, name: 'Start Button', isStartButton: true },
        { id: 6, name: 'Product 6', image: newImages[4] || allImages[4] },
        { id: 7, name: 'Product 7', image: newImages[5] || allImages[5] },
        { id: 8, name: 'Product 8', image: newImages[6] || allImages[6] },
        { id: 9, name: 'Product 9', image: newImages[7] || allImages[7] },
      ]);
    }
  };

  const handleClosePopup = () => {
    setIsComplete(false);
    setPopupDismissed(true);
    setPopupMessage(null);
  };

  if (loading) {
    return (
      <div className="optimization-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="optimization-page">
      {/* Top Navigation Bar */}
      <div className="optimization-top-bar">
        <div className="optimization-back-btn" onClick={onBack}>
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="optimization-title-text">Optimization</div>
        <div className="optimization-spacer"></div>
      </div>

      <div className="optimization-header">
        <div className="optimization-header-gradient"></div>
        <div className="container-fluid px-3 py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="optimization-title">
              <i className="bi bi-lightning-charge-fill me-2"></i>
              Start Optimization
            </div>
            <div className="optimization-counter">
              <span className="counter-label">Progress</span>
              <span className="counter-value">{currentTask}/{totalTasks}</span>
              <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.8 }}>
                Optimization {optimizationData?.currentOptimization || 1}/{maxOptimizations} ({vipLevel})
              </div>
            </div>
          </div>
          
        </div>
      </div>

      {showOptimizeDone && (
        <div className="optimize-done-message">
          <div className="optimize-done-content">
            <i className="bi bi-check-circle-fill text-success me-2"></i>
            Task Submission Completed
          </div>
        </div>
      )}


      <div className="optimization-content">
        <div className="container-fluid px-3">
          <div className="product-grid" style={{ position: 'relative' }}>
            {/* Previous Rating Message Overlay - Shows when user with pending task tries to start task */}
            {showPreviousRatingMessage && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                borderRadius: '12px'
              }}>
                <div style={{
                  backgroundColor: '#4a5568',
                  color: 'white',
                  padding: '20px 30px',
                  borderRadius: '12px',
                  maxWidth: '90%',
                  textAlign: 'center',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                }}>
                  <p style={{ 
                    color: 'white', 
                    fontSize: '1.1rem',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    Kindly submit the previous rating before you proceed to the next rating!
                  </p>
                </div>
              </div>
            )}
            
            {productCards.map((product, index) => (
              <div 
                key={product.id} 
                className="product-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {product.isStartButton ? (
                  <>
                    <button
                      className="start-button"
                      onClick={handleStart}
                      disabled={
                        isLocked || 
                        isComplete || 
                        currentTask >= totalTasks || 
                        !optimizationData ||
                        hasKnifeApplied ||
                        knifeRemovedPendingSubmit ||
                        !hasSufficientBalance
                      }
                      style={{
                        opacity: (hasKnifeApplied || knifeRemovedPendingSubmit || !hasSufficientBalance) ? 0.6 : 1,
                        cursor: (hasKnifeApplied || knifeRemovedPendingSubmit || !hasSufficientBalance) ? 'not-allowed' : 'pointer',
                        background: hasKnifeApplied ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 
                                    knifeRemovedPendingSubmit ? 'linear-gradient(135deg, #f59e0b, #d97706)' :
                                    !hasSufficientBalance ? 'linear-gradient(135deg, #f59e0b, #d97706)' : undefined,
                        color: (hasKnifeApplied || knifeRemovedPendingSubmit || !hasSufficientBalance) ? 'white' : undefined,
                        border: hasKnifeApplied ? '2px solid #dc2626' : 
                                knifeRemovedPendingSubmit ? '2px solid #d97706' :
                                !hasSufficientBalance ? '2px solid #d97706' : undefined
                      }}
                      title={hasKnifeApplied ? 'Knife Applied - Please deposit to unlock' : 
                             knifeRemovedPendingSubmit ? 'Please submit in Deposit page to enable tasks' :
                             !hasSufficientBalance ? `Insufficient Balance - Need $${MIN_BALANCE_FOR_OPTIMIZATION} Minimum` : undefined}
                    >
                      {!optimizationData ? 'Loading...' : 
                       hasKnifeApplied ? '🔒 Locked - Knife Applied' :
                       knifeRemovedPendingSubmit ? '🔒 Pending Submit' :
                       !hasSufficientBalance ? `🔒 Insufficient Balance` :
                       isLocked ? 'Locked' : 
                       isComplete ? 'Completed' : 
                       currentTask >= totalTasks ? 'Completed' : 
                       'Start Task'}
                    </button>
                  </>
                ) : (
                  <div className="product-card-inner">
                    <div className="product-card-overlay"></div>
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="product-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="product-placeholder" style={{ display: 'none' }}>
                      <i className="bi bi-image fs-1 text-muted"></i>
                    </div>
                    <div className="product-card-shine"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Task Submission Popup */}
      <TaskSubmissionPopup
        isOpen={showTaskPopup && !showPreviousRatingMessage}
        onClose={() => {
          setShowTaskPopup(false);
          setHasPendingTask(false);
          setShowPreviousRatingMessage(false);
        }}
        userId={user?.uid}
        vipLevel={vipLevel}
        onTaskComplete={(taskResult) => {
          setHasPendingTask(false);
          setShowPreviousRatingMessage(false);
          handleTaskComplete(taskResult);
        }}
        currentOptimization={optimizationData?.currentOptimization || 1}
        currentTask={currentTask + 1}
        totalTasks={totalTasks}
        onNavigateToDeposit={onNavigateToDeposit}
      />

      {/* Customer Service Popup */}
      {showCustomerServicePopup && (
        <div className="service-popup-overlay" onClick={() => setShowCustomerServicePopup(false)}>
          <div className="service-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                <i className="bi bi-headset me-2"></i>
                Contact Customer Service
              </h5>
              <button
                onClick={() => setShowCustomerServicePopup(false)}
                className="btn btn-sm btn-outline-secondary"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="alert alert-info mb-3">
              <i className="bi bi-info-circle me-2"></i>
              Optimization {optimizationData?.currentOptimization || 1} completed! 
              {nextOptimization && ` Please contact customer service to unlock Optimization ${nextOptimization}.`}
            </div>
            <CustomerServicePopup 
              onClose={() => setShowCustomerServicePopup(false)}
            />
          </div>
        </div>
      )}

      {/* Completion Popup - Needs Unlock */}
      {isComplete && !popupDismissed && popupMessage === 'needsUnlock' && !showCustomerServicePopup && (
        <div className="completion-message">
          <div className="completion-content" style={{ position: 'relative' }}>
            <button
              onClick={handleClosePopup}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6c757d',
                padding: '5px 10px',
                lineHeight: '1',
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <i className="bi bi-lock-fill text-warning fs-1 mb-3"></i>
            <h3>Optimization Complete!</h3>
            <p>All {totalTasks} tasks have been completed successfully.</p>
            <div className="alert alert-warning mt-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Please contact customer service to unlock Optimization {nextOptimization || optimizationData?.currentOptimization + 1 || 2}.
            </div>
            <div className="d-flex justify-content-center" style={{ marginTop: '15px' }}>
              <button 
                className="btn btn-primary me-2" 
                onClick={() => setShowCustomerServicePopup(true)}
              >
                <i className="bi bi-headset me-2"></i>
                Contact Customer Service
              </button>
              {onBack && (
                <button className="btn btn-outline-secondary" onClick={onBack}>
                  Back to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Completion Popup - Daily Limit Reached */}
      {isComplete && !popupDismissed && popupMessage === 'dailyLimit' && (
        <div className="completion-message">
          <div className="completion-content" style={{ position: 'relative' }}>
            <button
              onClick={handleClosePopup}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6c757d',
                padding: '5px 10px',
                lineHeight: '1',
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <i className="bi bi-check-circle-fill text-success fs-1 mb-3"></i>
            <h3>Optimization Complete!</h3>
            <p>All {totalTasks} tasks have been completed successfully.</p>
            <div className="alert alert-info mt-3">
              <i className="bi bi-calendar-x me-2"></i>
              All optimizations completed for today. Please try again tomorrow.
            </div>
            <div className="d-flex justify-content-center" style={{ marginTop: '15px' }}>
              {onBack && (
                <button className="btn btn-outline-secondary" onClick={onBack}>
                  Back to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="optimization-footer">
        <div className="container-fluid px-3 py-4">
          <div className="notice-section">
            <h5 className="notice-title">Notice</h5>
            <p className="notice-text">Online Support Hours 8:30 - 21:30</p>
            <p className="notice-text">Please contact online support for your assistance!</p>
          </div>
          <div className="footer-logo">
            <img src="/logos/logo.png" alt="CIRCANA Logo" className="footer-logo-img" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Optimization;
